import { useCallback, useRef, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { CalloutContainer } from '@frontend/schedule-operatory-availability-banner';
import { theme } from '@frontend/theme';
import { ContentSwitcher } from '../../../../components/content-switcher';
import { useCalendarStore } from '../../../../stores/calendar.store';
import { AppointmentListFilterButton } from '../../../appointment-list-filter-button';
import { CalendarDateSelector } from './calendar-date-selector';
import { FindAvailabilityButton } from './find-availability-button';
import { LocationChip } from './location-chip-container';
import { NewEventButton } from './new-event-button';
import { QuickFiltersContainer } from './quick-filters-container';

const BREAKPOINTS = {
  xsmall: 400,
  small: 500,
  medium: 600,
  large: 700,
};

// Custom hook for multiple container query breakpoints
// This provides reactive variables based on the container's width rather than viewport width
const useCalendarHeaderContainerQueries = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const resizeObserverRef = useRef<ResizeObserver>();

  const updateContainerWidth = useCallback((entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      const elementWidth = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      setContainerWidth(elementWidth);
    }
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Create ResizeObserver to watch container width changes
    resizeObserverRef.current = new ResizeObserver(updateContainerWidth);
    resizeObserverRef.current.observe(element);

    // Set initial width
    setContainerWidth(element.getBoundingClientRect().width);

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [updateContainerWidth]);

  // Calculate breakpoint states based on container width
  const containerQueries = {
    // Raw width value for custom logic
    width: containerWidth,

    // Breakpoint states
    isXSmall: containerWidth > 0 && containerWidth <= BREAKPOINTS.xsmall,
    isSmall: containerWidth > 0 && containerWidth <= BREAKPOINTS.small,
    isMedium: containerWidth > 0 && containerWidth <= BREAKPOINTS.medium,
    isLarge: containerWidth > 0 && containerWidth <= BREAKPOINTS.large,
    isXLarge: containerWidth > 0 && containerWidth > BREAKPOINTS.large,
  };

  return [containerQueries, containerRef] as const;
};

export const CalendarHeaderContainer = () => {
  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM();
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();
  const { isForSchedulePulse } = useCalendarStore(['isForSchedulePulse']);
  const xSmallMaxSize = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const { canViewBridgelineScheduleSettings } = useAiScheduleAccess();

  // TODO: Handle breakpoints with Content Switcher component
  const [containerQueries, containerRef] = useCalendarHeaderContainerQueries();
  const { calendarEntityType, setCalendarEntityType } = useCalendarStore([
    'calendarEntityType',
    'setCalendarEntityType',
  ]);

  if (isForSchedulePulse) {
    return (
      <div css={styles.container} ref={containerRef}>
        <div css={[styles.group, xSmallMaxSize && { flex: 1 }]}>
          <CalendarDateSelector />
        </div>
        <div css={styles.group}>
          <AppointmentListFilterButton iconOnly filterContentVariant={'popover'} />
        </div>
      </div>
    );
  }

  const shouldShowNewEventButton = hasWSM && xSmallMaxSize;
  const shouldShowQuickFilters = !(containerQueries.isSmall || xSmallMaxSize);
  const shouldShowContentSwitcher = !(containerQueries.isSmall || xSmallMaxSize) && isV3OperatorySupported;

  return (
    <div css={styles.container} ref={containerRef}>
      <div css={[styles.group, xSmallMaxSize && { flex: 1 }]}>
        <CalendarDateSelector />
        <LocationChip />
      </div>
      <div css={styles.group}>
        {shouldShowContentSwitcher && (
          <CalloutContainer selectedOption={calendarEntityType}>
            <ContentSwitcher
              selectedOption={calendarEntityType}
              onOptionChange={(value) => setCalendarEntityType(value)}
            />
          </CalloutContainer>
        )}
      </div>
      <div css={styles.group}>
        {shouldShowQuickFilters && <QuickFiltersContainer />}
        {canViewBridgelineScheduleSettings && <FindAvailabilityButton />}
        <AppointmentListFilterButton iconOnly={containerQueries.isMedium} filterContentVariant={'drawer'} />
        {shouldShowNewEventButton && <NewEventButton />}
      </div>
    </div>
  );
};

const styles = {
  container: css`
    background: ${theme.colors.white};
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(1)};
    gap: ${theme.spacing(0.5)};
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  group: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
};
